import { useState, useEffect } from "react";
import API from "@/lib/api";
import { formatCurrency, formatPercent } from "@/lib/formatters";
import MetricCard from "@/components/shared/MetricCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Shield, AlertTriangle, TrendingDown, Activity, Plus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import AddEditDialog from "@/components/shared/AddEditDialog";
import UploadDialog from "@/components/shared/UploadDialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as ReTooltip, Cell,
} from "recharts";

const SEVERITY_COLORS = { Critical: "#EF4444", High: "#F59E0B", Medium: "#3B82F6", Low: "#10B981" };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#151B24] border border-[#2D3748] rounded-sm p-3 text-sm shadow-xl">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-data text-xs" style={{ color: p.fill }}>{p.dataKey}: {p.value}%</p>
      ))}
    </div>
  );
};

export default function RiskAnalytics() {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    API.get("/risk/scenarios")
      .then((res) => setScenarios(res.data.scenarios || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const criticalCount = scenarios.filter((s) => s.severity === "Critical").length;
  const highCount = scenarios.filter((s) => s.severity === "High").length;
  const maxImpact = scenarios.length ? Math.min(...scenarios.map((s) => s.portfolio_value_impact)) : 0;

  if (loading) return <div className="p-6"><Skeleton className="h-96" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="risk-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Risk Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Stress scenarios and portfolio risk exposure</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-risk-btn" className="gap-1"><Plus size={14} />Add</Button>
          <Button size="sm" variant="secondary" onClick={() => setShowUpload(true)} data-testid="upload-risk-btn" className="gap-1"><Upload size={14} />Upload</Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <MetricCard label="Scenarios" value={scenarios.length} icon={Shield} />
        <MetricCard label="Critical Risks" value={criticalCount} icon={AlertTriangle} />
        <MetricCard label="High Risks" value={highCount} icon={Activity} />
        <MetricCard label="Max Impact" value={`${maxImpact}%`} icon={TrendingDown} />
      </div>

      <Card className="border border-border bg-card">
        <CardHeader className="border-b border-border p-3">
          <CardTitle className="text-sm font-semibold">Portfolio Value Impact by Scenario</CardTitle>
        </CardHeader>
        <CardContent className="p-4" data-testid="risk-impact-chart">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={scenarios} margin={{ bottom: 50 }}>
              <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 10 }} angle={-30} textAnchor="end" axisLine={false} tickLine={false} interval={0} />
              <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
              <ReTooltip content={<CustomTooltip />} />
              <Bar dataKey="portfolio_value_impact" radius={[2, 2, 0, 0]} barSize={36}>
                {scenarios.map((s, i) => (
                  <Cell key={i} fill={SEVERITY_COLORS[s.severity] || "#3B82F6"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4">
        {scenarios.map((scenario, idx) => (
          <Card key={idx} className="border border-border bg-card" data-testid={`risk-scenario-${idx}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">{scenario.name}</h3>
                    <Badge
                      className="font-data text-xs"
                      style={{
                        backgroundColor: `${SEVERITY_COLORS[scenario.severity]}20`,
                        color: SEVERITY_COLORS[scenario.severity],
                        borderColor: `${SEVERITY_COLORS[scenario.severity]}40`,
                      }}
                    >
                      {scenario.severity}
                    </Badge>
                    <Badge variant="secondary" className="font-data text-xs">
                      Probability: {scenario.probability}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{scenario.description}</p>
                  <div className="flex gap-6 mt-2">
                    <div>
                      <span className="text-xs text-muted-foreground">Parameter Change</span>
                      <p className="text-sm font-data font-medium text-amber-500">{scenario.change}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Portfolio Impact</span>
                      <p className="text-sm font-data font-medium text-red-500">{scenario.portfolio_value_impact}%</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">NOI Impact</span>
                      <p className="text-sm font-data font-medium text-red-500">{scenario.noi_impact}%</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">NAV Impact</span>
                      <p className="text-sm font-data font-medium text-red-500">{formatCurrency(scenario.nav_impact)}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-1">
                    {scenario.affected_sectors?.map((s) => (
                      <Badge key={s} variant="secondary" className="font-data text-xs">{s}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <AddEditDialog open={showAdd} entityType="risk_scenarios" onClose={() => setShowAdd(false)} onSave={refresh} />
      <UploadDialog open={showUpload} entityType="risk_scenarios" onClose={() => setShowUpload(false)} onSuccess={refresh} />
    </div>
  );
}
